import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, ApiError, download } from "../api";
import { fmtDate, fmtINR } from "../fmt";

interface Account {
  id: string;
  kind: "bank" | "credit_card" | "cash" | "wallet" | "paylater";
  name: string;
}

interface SelectedAccount {
  accountId: string;
  name: string;
  kind: string;
}

interface LedgerRow {
  id: string;
  date: string;
  accountId: string;
  bankName: string;
  narration: string | null;
  drcr: "dr" | "cr";
  amount: number;
}

interface RunningAccountResult {
  banks: SelectedAccount[];
  rows: LedgerRow[];
  openingBefore: number;
}

const KIND_LABELS: Record<string, string> = {
  bank: "Bank",
  credit_card: "Credit card",
  cash: "Cash",
  wallet: "Wallet",
  paylater: "Pay later",
};

export function RunningAccount() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [accounts, setAccounts] = useState<Account[] | null>(null);
  const [from, setFrom] = useState(searchParams.get("from") ?? "");
  const [to, setTo] = useState(searchParams.get("to") ?? "");
  // null = "not initialised yet" → default to all bank accounts once loaded.
  const [selected, setSelected] = useState<Set<string> | null>(null);
  const [data, setData] = useState<RunningAccountResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Load the account list for the picker (separate from the ledger response so
  // an empty selection still leaves accounts to re-tick).
  useEffect(() => {
    api
      .get<Account[]>("/api/accounts")
      .then(setAccounts)
      .catch((err) =>
        setError(err instanceof ApiError ? err.message : "failed to load accounts")
      );
  }, []);

  // Initialise the selection once, from the URL if present, else all banks.
  useEffect(() => {
    if (!accounts || selected !== null) return;
    const urlIds = searchParams.get("accountIds");
    if (urlIds !== null) {
      setSelected(new Set(urlIds.split(",").filter(Boolean)));
    } else {
      setSelected(new Set(accounts.filter((a) => a.kind === "bank").map((a) => a.id)));
    }
  }, [accounts, selected, searchParams]);

  // Fetch the ledger and keep the URL in sync with the current selection/range.
  useEffect(() => {
    if (selected === null) return;
    const ids = [...selected];
    const qs = new URLSearchParams();
    qs.set("accountIds", ids.join(","));
    if (from) qs.set("from", from);
    if (to) qs.set("to", to);
    setSearchParams(qs, { replace: true });

    setLoading(true);
    api
      .get<RunningAccountResult>(`/api/running-account?${qs.toString()}`)
      .then((d) => {
        setData(d);
        setError(null);
      })
      .catch((err) =>
        setError(err instanceof ApiError ? err.message : "failed to load running account")
      )
      .finally(() => setLoading(false));
    // setSearchParams is referentially stable; deliberately not a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, from, to]);

  const toggleAccount = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev ?? []);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    if (!accounts) return;
    setSelected(new Set(accounts.map((a) => a.id)));
  };

  const onlyBanks = () => {
    if (!accounts) return;
    setSelected(new Set(accounts.filter((a) => a.kind === "bank").map((a) => a.id)));
  };

  const onExport = async () => {
    if (selected === null) return;
    try {
      const qs = new URLSearchParams();
      qs.set("accountIds", [...selected].join(","));
      if (from) qs.set("from", from);
      if (to) qs.set("to", to);
      await download(`/api/running-account.xlsx?${qs.toString()}`, "running-account.xlsx");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "export failed");
    }
  };

  // Group accounts by kind for the picker.
  const grouped = useMemo(() => {
    const map = new Map<string, Account[]>();
    for (const a of accounts ?? []) {
      if (!map.has(a.kind)) map.set(a.kind, []);
      map.get(a.kind)!.push(a);
    }
    return [...map.entries()];
  }, [accounts]);

  const banks = data?.banks ?? [];

  // Compute the combined running balance client-side, in the exact
  // server-returned order (date, created_at, id) — never re-sort here.
  const rowsWithRunning = useMemo(() => {
    if (!data) return [];
    let running = data.openingBefore;
    return data.rows.map((r) => {
      running += r.drcr === "cr" ? r.amount : -r.amount;
      return { ...r, running };
    });
  }, [data]);

  const perAccountNet = useMemo(() => {
    const net = new Map<string, number>();
    for (const b of banks) net.set(b.accountId, 0);
    for (const r of data?.rows ?? []) {
      net.set(r.accountId, (net.get(r.accountId) ?? 0) + (r.drcr === "cr" ? r.amount : -r.amount));
    }
    return net;
  }, [data, banks]);

  const finalBalance = rowsWithRunning.length
    ? rowsWithRunning[rowsWithRunning.length - 1].running
    : (data?.openingBefore ?? 0);

  return (
    <div>
      <h1>Running Account</h1>
      {error && <div className="error-text">{error}</div>}

      <div className="toolbar">
        <div className="field">
          <label htmlFor="ra-from">From</label>
          <input id="ra-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="ra-to">To</label>
          <input id="ra-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
        <button type="button" onClick={onlyBanks}>
          Only banks
        </button>
        <button type="button" onClick={selectAll}>
          Select all
        </button>
        <button type="button" onClick={onExport} disabled={banks.length === 0}>
          Export to Excel
        </button>
      </div>

      <div className="ra-picker">
        {grouped.map(([kind, accs]) => (
          <fieldset key={kind} className="ra-picker-group">
            <legend>{KIND_LABELS[kind] ?? kind}</legend>
            {accs.map((a) => (
              <label key={a.id} className="ra-picker-item">
                <input
                  type="checkbox"
                  checked={selected?.has(a.id) ?? false}
                  onChange={() => toggleAccount(a.id)}
                />
                {a.name}
              </label>
            ))}
          </fieldset>
        ))}
        {accounts && accounts.length === 0 && (
          <div className="empty-state">No accounts yet. Add accounts in Settings.</div>
        )}
      </div>

      {loading && <div className="empty-state">Loading…</div>}

      {!loading && data && banks.length === 0 && (
        <div className="empty-state">Select at least one account to view its running account.</div>
      )}

      {!loading && data && banks.length > 0 && (
        <div className="ra-wrap">
          <table className="ra-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Narration</th>
                {banks.map((b) => (
                  <th key={b.accountId} className="num">
                    {b.name}
                    <span className="ra-kind"> ({KIND_LABELS[b.kind] ?? b.kind})</span>
                  </th>
                ))}
                <th className="num">Running Balance</th>
              </tr>
            </thead>
            <tbody>
              {data.openingBefore !== 0 && (
                <tr className="ra-opening">
                  <td></td>
                  <td>Opening balance</td>
                  {banks.map((b) => (
                    <td key={b.accountId} className="num"></td>
                  ))}
                  <td className="num">{fmtINR(data.openingBefore)}</td>
                </tr>
              )}
              {rowsWithRunning.map((r) => (
                <tr key={r.id}>
                  <td>{fmtDate(r.date)}</td>
                  <td>{r.narration ?? ""}</td>
                  {banks.map((b) => (
                    <td key={b.accountId} className="num">
                      {b.accountId === r.accountId ? (
                        <span className={r.drcr === "dr" ? "amt-dr" : "amt-cr"}>
                          {fmtINR(r.amount)} {r.drcr.toUpperCase()}
                        </span>
                      ) : (
                        ""
                      )}
                    </td>
                  ))}
                  <td className="num">{fmtINR(r.running)}</td>
                </tr>
              ))}
              {rowsWithRunning.length === 0 && (
                <tr>
                  <td colSpan={2 + banks.length + 1} className="empty-state">
                    No transactions in this range.
                  </td>
                </tr>
              )}
            </tbody>
            <tfoot>
              <tr className="ra-totals">
                <td></td>
                <td>Net per account</td>
                {banks.map((b) => (
                  <td key={b.accountId} className="num">
                    {fmtINR(perAccountNet.get(b.accountId) ?? 0)}
                  </td>
                ))}
                <td className="num">{fmtINR(finalBalance)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}
